import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from '@pec/aion-ui-core/helpers/querystring';
import { Box, Button, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { ACCOUNT_KEY } from '../helpers/registerSurveyCallbacks';
import { checkCompletionOnServer } from '../helpers/complete';
import { SELECTED_PRODUCT_ICON } from './questionTypes/MuiSubscriptions';
import { MainLayout } from '../containers/MainLayout';
import { Alert, AlertTitle } from '@material-ui/lab';

const useStyles = makeStyles(theme =>
  createStyles({
    contentContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(8),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3)
      },
      '& > img': {
        [theme.breakpoints.down('md')]: {
          width: 80,
          height: 80
        }
      },
      '& > div': {
        paddingLeft: theme.spacing(4),
        [theme.breakpoints.down('md')]: {
          paddingLeft: theme.spacing(3)
        }
      },
      '& h4': {
        [theme.breakpoints.down('md')]: {
          fontSize: 24,
          lineHeight: '32px'
        },
        '& + p': {
          [theme.breakpoints.down('md')]: {
            fontSize: 16,
            lineHeight: '20px'
          }
        }
      }
    },
    button: {
      textTransform: 'none',
      borderRadius: '100px',
      color: 'var(--primary)',
      borderColor: 'var(--light-outline)',
      height: 38,
      fontWeight: 600,
      letterSpacing: '0.006em',
      marginTop: theme.spacing(1)
    },
    paymentMethod: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
      '& h5, & h6, & a': {
        fontWeight: 700
      },
      '& h6': {
        margin: '16px 0'
      }
    }
  })
);

export const SuccessScreen: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const { search } = useLocation();
  const history = useHistory();
  const { email, userExists, foundation, withoutPayment, country, invoiceUrl } = parse(search);
  const invoiceLink = invoiceUrl ? decodeURIComponent(invoiceUrl as string) : undefined;
  const selectedProductIconSrc = React.useRef(sessionStorage.getItem(SELECTED_PRODUCT_ICON));
  let optionalParams = '';

  if (foundation) {
    optionalParams = '?foundation=true';
  } else if (country) {
    optionalParams = '?country=' + country;
  }

  React.useEffect(() => {
    (async () => {
      let purchaseResolve: (value?: unknown) => void = () => {};
      const purchasePromise = new Promise(resolve => {
        purchaseResolve = resolve;
      });

      let gtmResolve: (value?: unknown) => void = () => {};
      const gtmPromise = new Promise(resolve => {
        gtmResolve = resolve;
      });

      if (window['dataLayer']) {
        gtmResolve();
      } else {
        window.addEventListener('gtmLoaded', () => gtmResolve(), { once: true });
      }

      const purchaseInfoJSON = sessionStorage.getItem('purchaseInfo');
      const purchaseInfo = purchaseInfoJSON && JSON.parse(purchaseInfoJSON);
      const account = sessionStorage.getItem(ACCOUNT_KEY);
      sessionStorage.clear();
      account && sessionStorage.setItem(ACCOUNT_KEY, account); // Keep ACCOUNT_KEY at this stage

      try {
        const email = parse(search).email as string;
        const accountId = JSON.parse(account as string)[email];

        if (withoutPayment !== 'true') {
          const res = await checkCompletionOnServer(accountId);

          if (res === false) {
            history.push('/failure');
          }
        }

        sessionStorage.removeItem(ACCOUNT_KEY);
        setIsLoading(false);
        purchaseResolve();
      } catch {
        history.push(`/${optionalParams}`);
      }

      Promise.all([purchasePromise, gtmPromise]).then(() => {
        const dataLayer = window['dataLayer'];
        try {
          if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push(purchaseInfo);
          }
        } catch {}
      });
    })();
  }, [search, history, optionalParams, withoutPayment]);

  return (
    <MainLayout>
      <>
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            <div className={classes.contentContainer}>
              <img
                width={139}
                height={139}
                src={`${process.env.PUBLIC_URL}${selectedProductIconSrc.current}`}
                alt="Selected product icon"
              />
              {JSON.parse(userExists as string) ? (
                <div>
                  <Typography variant="h4">
                    {t('signup:success.registrationSuccessful', 'Registration successful!')}
                  </Typography>
                  <Button
                    component="a"
                    className={classes.button}
                    variant="outlined"
                    href="https://veriforceone.com/identity/Account/Login"
                  >
                    {t('signup:success.logIn', 'Log in to Veriforce CHAS')}
                  </Button>
                </div>
              ) : (
                <div>
                  <Typography variant="h4">{t('signup:success.thankYou', 'Thank you for joining CHAS!')}</Typography>
                  {email && (
                    <Typography variant="body1">
                      {t('signup:success.instructions', {
                        defaultValue:
                          'An email has been sent to {{email}} with instructions to complete your account setup.',
                        email
                      })}
                    </Typography>
                  )}
                </div>
              )}
            </div>
            {invoiceLink && (
              <Box className={classes.paymentMethod}>
                <Typography variant="h5">
                  {t('signup:success.bankTransfer.instructions', 'Bank Transfer Payment Instructions')}
                </Typography>
                <Typography variant="subtitle2">
                  {t(
                    'signup:success.bankTransfer.followLink',
                    'Please follow the link below to your payment instructions:'
                  )}
                </Typography>
                <Button
                  component="a"
                  variant="contained"
                  href={`${invoiceLink}`}
                  target="_blank"
                  color="primary"
                  size="large"
                >
                  {t('signup:success.bankTransfer.viewBankTransfer', 'View Bank Transfer Details')}
                </Button>
                <Button
                  component="a"
                  variant="text"
                  href={`${invoiceLink}`}
                  target="_blank"
                  color="primary"
                  style={{
                    margin: '16px 0',
                    textTransform: 'none'
                  }}
                >
                  {t('signup:success.bankTransfer.payByCard', 'Pay by credit/debit card instead')}
                </Button>
                <Alert severity={'warning'}>
                  <AlertTitle>
                    {t('signup:success.bankTransfer.important', 'Important: Please make payment as soon as possible.')}
                  </AlertTitle>
                  <Typography variant="body2">
                    <Trans i18nKey="signup:success.bankTransfer.questionnaire">
                      Until payment is made, you will <strong>not</strong> be able to submit your questionnaire.
                    </Trans>
                  </Typography>
                </Alert>
              </Box>
            )}
          </>
        )}
      </>
    </MainLayout>
  );
};
